import { nullableNumber, requiredString } from "common";
import { compact } from "lodash";
import { useMemo } from "react";
import {
    AllFilter,
    AmountType,
    AndFilter,
    FeeScope,
    FeeScopeModifier,
    InFilter,
    InProductScheduleFilter,
    LineItemTemplate,
    LineItemType,
    OrFilter,
    Permission,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleFilterObject,
    PurchaseRuleLink,
    PurchaseRuleShortcutTemplateCode,
    PurchaseRuleShortcutType,
    RegexMatchFilter,
    RuleFilterItem,
    SelectedExperiences,
    SelectedMemberships,
    SelectedMerchandise,
    SelectionState,
    TaxesAndFeesSource,
} from "types";
import { array, boolean, number, object } from "yup";
import { getSelectedItems } from "./TaxAndFeeFormValuesHelper";
import { useProducts } from "@/hooks/resources/useProduct";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useResource } from "@/hooks/useResource";

const purchaseSourceValues = Object.values(TaxesAndFeesSource);

export interface TaxAndFeeFormValues {
    title: string;
    type: LineItemType.TAX | LineItemType.FEE;
    amountType: AmountType;
    amount: number | null;
    percent: number | null;
    scope: FeeScope;
    scopeModifierPerPerson: FeeScopeModifier.PER_DAY | "";
    scopeModifierPerOuting: FeeScopeModifier[];
    selectedExperiences: SelectedExperiences;
    selectedMemberships: SelectedMemberships;
    selectedMerchandise: SelectedMerchandise;
    selectedDemographics: SelectionState;
    selectedEquipment: SelectionState;
    selectedAddOns: SelectionState;
    selectedPartners: SelectionState;
    selectedOperators: SelectionState;
    purchaseSources: string[];
    isAppAndIntegrationEnabled: boolean;
}

const initialValues: TaxAndFeeFormValues = {
    title: "",
    type: LineItemType.FEE,
    amountType: AmountType.ABSOLUTE,
    amount: null,
    percent: null,
    scope: FeeScope.PERSON,
    scopeModifierPerPerson: "",
    scopeModifierPerOuting: [],
    selectedExperiences: { all: true, experiences: [] },
    selectedMemberships: { all: true, memberships: [] },
    selectedMerchandise: { all: true, merchandise: [] },
    selectedDemographics: { all: true, selected: [] },
    selectedEquipment: { all: true, selected: [] },
    selectedAddOns: { all: true, selected: [] },
    selectedPartners: { all: true, selected: [] },
    selectedOperators: { all: true, selected: [] },
    purchaseSources: [...purchaseSourceValues],
    isAppAndIntegrationEnabled: true,
};

interface GetDefaultTaxAndFeeFormValuesParams {
    isMembershipViewGranted: boolean;
    isMerchandiseViewGranted: boolean;
    defaultMerchandiseId?: ID;
}

export const getDefaultTaxAndFeeFormValues = ({
    isMembershipViewGranted,
    isMerchandiseViewGranted,
    defaultMerchandiseId,
}: GetDefaultTaxAndFeeFormValuesParams): TaxAndFeeFormValues => {
    const values: TaxAndFeeFormValues = {
        ...initialValues,
        purchaseSources: [...initialValues.purchaseSources],
        scopeModifierPerOuting: [...initialValues.scopeModifierPerOuting],
        selectedExperiences: { ...initialValues.selectedExperiences },
        selectedMemberships: { ...initialValues.selectedMemberships },
        selectedMerchandise: { ...initialValues.selectedMerchandise },
        selectedDemographics: {
            ...initialValues.selectedDemographics,
            selected: [...initialValues.selectedDemographics.selected],
        },
        selectedAddOns: { ...initialValues.selectedAddOns, selected: [...initialValues.selectedAddOns.selected] },
        selectedPartners: { ...initialValues.selectedPartners, selected: [...initialValues.selectedPartners.selected] },
        selectedOperators: {
            ...initialValues.selectedOperators,
            selected: [...initialValues.selectedOperators.selected],
        },
    };

    if (defaultMerchandiseId && isMerchandiseViewGranted) {
        values.scope = FeeScope.MERCHANDISE;
        values.selectedExperiences = { all: false, experiences: [] };
        values.selectedMemberships = { all: false, memberships: [] };
        values.selectedMerchandise = { all: false, merchandise: [defaultMerchandiseId] };
    }

    if (!isMembershipViewGranted) {
        values.selectedMemberships = { all: false, memberships: [] };
    }

    if (!isMerchandiseViewGranted) {
        values.selectedMerchandise = { all: false, merchandise: [] };
    }

    return values;
};

export const validationSchema = object()
    .shape({
        title: requiredString(),
        amount: number()
            .nullable()
            .when("amountType", {
                is: (amountType: AmountType) => amountType === AmountType.ABSOLUTE,
                then: nullableNumber().required().positive(),
            }),
        percent: number()
            .nullable()
            .when("amountType", {
                is: (amountType: AmountType) => amountType === AmountType.PERCENT,
                then: nullableNumber().required().positive().max(100, "must be <= 100"),
            }),
        // Need atleast one Purchase source or App And Integration Enabled.
        purchaseSources: array().when("isAppAndIntegrationEnabled", {
            // If App And Integration not enabled, checking
            is: (isAppAndIntegrationEnabled: boolean) => !isAppAndIntegrationEnabled,
            then: array().min(1, "is required"),
        }),
        selectedDemographics: object().shape({
            all: boolean(),
            selected: array().when("all", {
                is: false,
                then: array().min(1, "is required"),
            }),
        }),
        selectedAddOns: object().shape({
            all: boolean(),
            selected: array().when("all", {
                is: false,
                then: array().min(1, "is required"),
            }),
        }),
        selectedExperiences: object().shape({
            all: boolean(),
            experiences: array(),
        }),
        selectedMemberships: object().shape({
            all: boolean(),
            memberships: array(),
        }),
        selectedMerchandise: object().shape({
            all: boolean(),
            merchandise: array(),
        }),
    })
    .test(
        "at-least-one-experiences-or-memberships-or-merchandise",
        "You must select at least one experience, membership or merchandise.",
        function (value) {
            const { selectedExperiences, selectedMemberships, selectedMerchandise } = value;

            const experiencesSelected =
                selectedExperiences?.all === true ||
                (Array.isArray(selectedExperiences?.experiences) && selectedExperiences.experiences.length > 0);

            const membershipsSelected =
                selectedMemberships?.all === true ||
                (Array.isArray(selectedMemberships?.memberships) && selectedMemberships.memberships.length > 0);

            const merchandiseSelected =
                selectedMerchandise?.all === true ||
                (Array.isArray(selectedMerchandise?.merchandise) && selectedMerchandise.merchandise.length > 0);

            if (experiencesSelected || membershipsSelected || merchandiseSelected) {
                return true;
            }

            // this will be common error for selectedExperiences, selectedMemberships and selectedMerchandise
            return this.createError({
                path: "selectedExperiences.experiences",
                message: "is required",
            });
        },
    );

/**
 * Convert purchaseRule to form values if exists or return default initialValues
 */
export const useTaxAndFeeFormValues = (
    purchaseRule: PurchaseRule | null,
    purchaseRuleLink?: PurchaseRuleLink | null,
    subTotalLineItemTemplates?: LineItemTemplate[],
    unitLineItemTemplates?: LineItemTemplate[],
    defaultMerchandiseId?: ID,
): TaxAndFeeFormValues => {
    const { data: lineItemTemplate } = useResource<LineItemTemplate>([
        "lineItemTemplates",
        purchaseRule?.actions[0].template?.id,
    ]);
    const { isGranted } = useAuthorization();
    const { products } = useProducts();

    const templateIds = purchaseRule?.actions[0].shortcut?.options?.templateIds;
    const { data: lineItemTemplateForType } = useResource<LineItemTemplate>(["lineItemTemplates", templateIds?.[0]]);

    const isMembershipViewGranted = isGranted(Permission.MEMBERSHIPS_VIEW);
    const isMerchandiseViewGranted = isGranted(Permission.MERCHANDISE_VIEW);
    const defaultValues = useMemo(
        () =>
            getDefaultTaxAndFeeFormValues({ isMembershipViewGranted, isMerchandiseViewGranted, defaultMerchandiseId }),
        [isMembershipViewGranted, isMerchandiseViewGranted, defaultMerchandiseId],
    );

    if (!purchaseRule) {
        return defaultValues;
    }

    const purchaseRuleAction = purchaseRule.actions[0];

    const { amountType, amount, percent } = getAmountValues(purchaseRuleAction);

    let { isAllSelected, selectedExperienceIds, selectedMembershipIds, selectedMerchandiseIds } = getSelectedItems(
        purchaseRule,
        purchaseRuleLink,
        products,
    );

    if (!isGranted(Permission.MEMBERSHIPS_VIEW)) {
        isAllSelected.memberships = false;
        selectedMembershipIds = [];
    }

    if (!isGranted(Permission.MERCHANDISE_VIEW)) {
        isAllSelected.merchandise = false;
        selectedMerchandiseIds = [];
    }

    return {
        ...initialValues,
        title: lineItemTemplate?.name ?? "",
        type: lineItemTemplate?.type === LineItemType.TAX ? LineItemType.TAX : LineItemType.FEE,
        amountType,
        amount,
        percent,
        ...getFeeScopeAndTemplateValues(
            purchaseRule,
            lineItemTemplateForType?.type,
            subTotalLineItemTemplates,
            unitLineItemTemplates,
        ),
        selectedExperiences: {
            all: isAllSelected.experiences,
            experiences: selectedExperienceIds,
        },
        selectedMemberships: {
            all: isAllSelected.memberships,
            memberships: selectedMembershipIds,
        },
        selectedMerchandise: {
            all: isAllSelected.merchandise,
            merchandise: selectedMerchandiseIds,
        },
        ...getPurchaseAndPartnerDataFromFilter(purchaseRule?.filter),
    };
};

const getAmountValues = (purchaseRuleAction: PurchaseRuleAction) => {
    const amountType =
        purchaseRuleAction.shortcut?.type === PurchaseRuleShortcutType.PERCENT
            ? AmountType.PERCENT
            : AmountType.ABSOLUTE;

    const amount = amountType === AmountType.ABSOLUTE ? purchaseRuleAction.shortcut!.amount : null;
    const percent = amountType === AmountType.PERCENT ? purchaseRuleAction.shortcut!.amount : null;

    return { amountType, amount, percent };
};

interface FeeScopeAndTemplateValuesProps {
    scope: FeeScope;
    scopeModifierPerPerson: FeeScopeModifier.PER_DAY | "";
    scopeModifierPerOuting: FeeScopeModifier[] | [];
    selectedDemographics: SelectionState;
    selectedEquipment: SelectionState;
    selectedAddOns: SelectionState;
}

export const getFeeScopeAndTemplateValues = (
    purchaseRule: PurchaseRule,
    lineItemType?: LineItemType,
    subTotalLineItemTemplates?: LineItemTemplate[],
    unitLineItemTemplates?: LineItemTemplate[],
): FeeScopeAndTemplateValuesProps => {
    const feeScopeAndTemplateValues: FeeScopeAndTemplateValuesProps = {
        scope: FeeScope.PERSON,
        scopeModifierPerPerson: "",
        scopeModifierPerOuting: [],
        selectedDemographics: { all: true, selected: [] },
        selectedEquipment: { all: true, selected: [] },
        selectedAddOns: { all: true, selected: [] },
    };

    const shortcutData = purchaseRule.actions[0].shortcut;
    const selectedTemplateIds = shortcutData?.options?.templateIds ?? [];
    const templateCode = shortcutData?.options?.templateCodes?.[0] ?? "";
    const selectedTemplateIdsByType =
        selectedTemplateIds.length > 0
            ? groupSelectedTemplateIdsByType(selectedTemplateIds, unitLineItemTemplates)
            : {};

    switch (shortcutData?.type) {
        case PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT_PER_DAY:
            feeScopeAndTemplateValues.scopeModifierPerPerson = FeeScopeModifier.PER_DAY;
            feeScopeAndTemplateValues.scope =
                templateCode === PurchaseRuleShortcutTemplateCode.EQUIPMENT || lineItemType === LineItemType.EQUIPMENT
                    ? FeeScope.EQUIPMENT
                    : FeeScope.PERSON;
            break;
        case PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT:
            if (
                templateCode === PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC ||
                lineItemType === LineItemType.DEMOGRAPHIC
            ) {
                feeScopeAndTemplateValues.scope = FeeScope.PERSON;
            } else if (
                templateCode === PurchaseRuleShortcutTemplateCode.EQUIPMENT ||
                lineItemType === LineItemType.EQUIPMENT
            ) {
                feeScopeAndTemplateValues.scope = FeeScope.EQUIPMENT;
            } else if (templateCode === PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL) {
                feeScopeAndTemplateValues.scope = FeeScope.MERCHANDISE;
            } else {
                feeScopeAndTemplateValues.scope = FeeScope.ADDON;
            }
            break;
        case PurchaseRuleShortcutType.ABSOLUTE_PER_PURCHASE:
            feeScopeAndTemplateValues.scope = FeeScope.OUTING;
            break;
        default:
            feeScopeAndTemplateValues.scope = FeeScope.PERSON;
    }

    if (shortcutData?.type === PurchaseRuleShortcutType.PERCENT) {
        const modifiers: FeeScopeModifier[] = [];
        const codes = shortcutData.options?.templateCodes ?? [];
        const templateCodes =
            codes.length === 0
                ? (getSystemTemplateCodesFromIds(
                      selectedTemplateIds,
                      subTotalLineItemTemplates,
                  ) as unknown as PurchaseRuleShortcutTemplateCode[])
                : [];

        if (selectedTemplateIdsByType[LineItemType.DEMOGRAPHIC]?.length > 0) {
            templateCodes.push(PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC);
        }
        if (selectedTemplateIdsByType[LineItemType.ADDON]?.length > 0) {
            templateCodes.push(PurchaseRuleShortcutTemplateCode.ADDON);
        }

        for (const code of [...codes, ...templateCodes]) {
            switch (code) {
                case PurchaseRuleShortcutTemplateCode.DEMOGRAPHIC:
                case PurchaseRuleShortcutTemplateCode.DISCOUNT_SUBTOTAL:
                    if (!modifiers.includes(FeeScopeModifier.EXCLUDE_ADDON)) {
                        modifiers.push(FeeScopeModifier.EXCLUDE_ADDON);
                    }
                    break;
                case PurchaseRuleShortcutTemplateCode.EQUIPMENT:
                    if (!modifiers.includes(FeeScopeModifier.EQUIPMENT_ONLY)) {
                        modifiers.push(FeeScopeModifier.EQUIPMENT_ONLY);
                    }
                    break;
                case PurchaseRuleShortcutTemplateCode.ADDON:
                    if (!modifiers.includes(FeeScopeModifier.ADDON_ONLY)) {
                        modifiers.push(FeeScopeModifier.ADDON_ONLY);
                    }
                    break;
                case PurchaseRuleShortcutTemplateCode.PICKUP_SUBTOTAL:
                    if (!modifiers.includes(FeeScopeModifier.PICKUP_ONLY)) {
                        modifiers.push(FeeScopeModifier.PICKUP_ONLY);
                    }
                    break;
                case PurchaseRuleShortcutTemplateCode.WARE_SUBTOTAL:
                    if (!modifiers.includes(FeeScopeModifier.WARE_ONLY)) {
                        modifiers.push(FeeScopeModifier.WARE_ONLY);
                    }
                    break;
                case PurchaseRuleShortcutTemplateCode.PRE_TAX_SUBTOTAL:
                case PurchaseRuleShortcutTemplateCode.PRE_TAX_FEE_SUBTOTAL:
                    if (!modifiers.includes(FeeScopeModifier.ALL)) {
                        modifiers.push(FeeScopeModifier.ALL);
                    }
                    break;
            }
        }

        feeScopeAndTemplateValues.scopeModifierPerOuting = modifiers;
    }

    if (selectedTemplateIdsByType[LineItemType.DEMOGRAPHIC]) {
        feeScopeAndTemplateValues.selectedDemographics.all = false;
        feeScopeAndTemplateValues.selectedDemographics.selected = selectedTemplateIdsByType[LineItemType.DEMOGRAPHIC];
    }

    if (selectedTemplateIdsByType[LineItemType.EQUIPMENT]) {
        feeScopeAndTemplateValues.selectedEquipment.all = false;
        feeScopeAndTemplateValues.selectedEquipment.selected = selectedTemplateIdsByType[LineItemType.EQUIPMENT];
    }

    if (selectedTemplateIdsByType[LineItemType.ADDON]) {
        feeScopeAndTemplateValues.selectedAddOns.all = false;
        feeScopeAndTemplateValues.selectedAddOns.selected = selectedTemplateIdsByType[LineItemType.ADDON];
    }

    return feeScopeAndTemplateValues;
};

export interface PurchaseAndPartnerData {
    selectedPartners: SelectionState;
    selectedOperators: SelectionState;
    purchaseSources: string[];
    isAppAndIntegrationEnabled: boolean;
}

export const getPurchaseAndPartnerDataFromFilter = (
    filterData?: AndFilter | InFilter | OrFilter | AllFilter | InProductScheduleFilter | null,
) => {
    const purchaseAndPartnerData: PurchaseAndPartnerData = {
        selectedPartners: { all: true, selected: [] },
        selectedOperators: { all: true, selected: [] },
        purchaseSources: [...purchaseSourceValues],
        isAppAndIntegrationEnabled: true,
    };

    if (filterData) {
        filterData = filterData as AndFilter;
        const inFilters = filterData.filters.filter(
            (item) => item.object === PurchaseRuleFilterObject.IN_FILTER,
        ) as InFilter[];

        const sourceOrFilter = filterData.filters
            .filter((item) => item.object === PurchaseRuleFilterObject.OR_FILTER)
            .find(
                (filter) =>
                    "filters" in filter &&
                    filter.filters?.find((filter) => "field" in filter && filter.field === "source"),
            ) as OrFilter;

        const purchaseSourcesInFilter =
            (sourceOrFilter?.filters.filter(
                (item) => item.object === PurchaseRuleFilterObject.IN_FILTER,
            ) as InFilter[]) ?? [];

        const purchaseSources = [...inFilters, ...purchaseSourcesInFilter].filter((item) => item.field === "source");

        if (purchaseSources.length > 0) {
            purchaseAndPartnerData.purchaseSources = purchaseSources[0].operand as string[];
        }

        const commercialChannelOrFilter = filterData.filters
            .filter((item) => item.object === PurchaseRuleFilterObject.OR_FILTER)
            .find(
                (filter) =>
                    "filters" in filter &&
                    filter.filters?.find(
                        (filter) =>
                            "field" in filter && ["partnerSeller.id", "affiliate.id"].includes(filter.field ?? ""),
                    ),
            );
        const commercialChannelInFilter =
            commercialChannelOrFilter && "filters" in commercialChannelOrFilter
                ? (commercialChannelOrFilter?.filters?.filter(
                      (item: RuleFilterItem) => item.object === PurchaseRuleFilterObject.IN_FILTER,
                  ) as InFilter[])
                : [];

        const agentData = [...inFilters, ...commercialChannelInFilter].filter((item) => item.field === "affiliate.id");
        if (agentData.length > 0) {
            const agentOperand = agentData[0].operand ?? [];

            purchaseAndPartnerData.selectedPartners.all = false;
            purchaseAndPartnerData.selectedPartners.selected = compact(agentOperand);
        }

        const operatorData = [...inFilters, ...commercialChannelInFilter].filter(
            (item) => item.field === "partnerSeller.id",
        );
        if (operatorData.length > 0) {
            const operatorOperand = operatorData[0].operand ?? [];

            purchaseAndPartnerData.selectedOperators.all = false;
            purchaseAndPartnerData.selectedOperators.selected = compact(operatorOperand);
        }

        const rootRegexFilter =
            filterData.filters.filter((item) => item.object === PurchaseRuleFilterObject.REGEX_MATCH_FILTER) ?? [];
        const childRegexFilter = sourceOrFilter
            ? sourceOrFilter?.filters.filter((item) => item.object === PurchaseRuleFilterObject.REGEX_MATCH_FILTER)
            : [];

        const regexFilters = [...rootRegexFilter, ...childRegexFilter] as RegexMatchFilter[];

        if (purchaseSources.length > 0) {
            // only if some purchaseSources is selected, check isAppAndIntegrationEnabled status
            purchaseAndPartnerData.isAppAndIntegrationEnabled = regexFilters.some((item) => item.field === "source");
        }
    }

    return purchaseAndPartnerData;
};

const getSystemTemplateCodesFromIds = (templateIds: ID[] = [], subTotalLineItemTemplates?: LineItemTemplate[]) => {
    const templateCodes = [];

    for (const id of templateIds) {
        const template = subTotalLineItemTemplates?.find((template) => template.id === id);
        if (template?.code) {
            templateCodes.push(template.code);
        }
    }

    return templateCodes;
};

const groupSelectedTemplateIdsByType = (templateIds: ID[] = [], unitLineItemTemplates: LineItemTemplate[] = []) => {
    const templateIdMap: Record<ID, LineItemType> = {};
    for (const template of unitLineItemTemplates) {
        templateIdMap[template.id] = template.type;
    }
    const groupedTemplateIds: Record<string, ID[]> = {};
    for (const id of templateIds) {
        const templateType = templateIdMap[id];
        if (templateType) {
            if (groupedTemplateIds[templateType]) {
                groupedTemplateIds[templateType].push(id);
            } else {
                groupedTemplateIds[templateType] = [id];
            }
        }
    }

    return groupedTemplateIds;
};
